import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  salutation?: string;
  institutionName?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  expiresIn?: number;
}

export interface JwtUserClaims {
  email: string;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly baseUrl = `${environment.apiUrl}/auth`;

  constructor(private http: HttpClient) {}

  register(req: RegisterRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/register`, req)
      .pipe(tap((res) => this.storeToken(res.token)));
  }

  login(req: LoginRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/login`, req)
      .pipe(tap((res) => this.storeToken(res.token)));
  }

  /**
   * Redeems the one-time code from the iOS Google sign-in session.
   * {@code verifier} is the secret kept in this app; the API checks its hash.
   */
  exchangeNativeOAuthCode(code: string, verifier: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/oauth/native`, { code, verifier })
      .pipe(tap((res) => this.storeToken(res.token)));
  }

  forgotPassword(email: string): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success?: boolean; message?: string }>(`${this.baseUrl}/forgot-password`, { email }).pipe(
      map((r) => ({
        success: r != null && r.success !== false,
        message: r != null && typeof (r as any).message === 'string' ? (r as any).message : undefined,
      })),
      catchError((err: HttpErrorResponse) =>
        of({
          success: false,
          message: err.error?.message || err.message || 'Request failed',
        })
      )
    );
  }

  resetPassword(token: string, newPassword: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/reset-password`, { token, newPassword })
      .pipe(tap((res) => this.storeToken(res.token)));
  }

  /**
   * Extends the session with a new JWT (same claims, new exp). Safe to call when opening Recording.
   * No-op if not logged in; failures are ignored (interceptor does not redirect on 401 for this URL).
   */
  refreshSessionIfLoggedIn(): Observable<boolean> {
    if (!this.getStoredToken()) {
      return of(false);
    }
    return this.http.post<AuthResponse>(`${this.baseUrl}/refresh`, {}).pipe(
      tap((res) => {
        if (res?.token) {
          this.storeToken(res.token);
        }
      }),
      map(() => true),
      catchError(() => of(false))
    );
  }

  private storeToken(token: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('jwt_token', token);
    }
  }

  getStoredToken(): string | null {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
  }

  /**
   * Returns email and name from the stored JWT. Safe in browser only; null when no token.
   */
  getJwtClaims(): JwtUserClaims | null {
    const token = this.getStoredToken();
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(payload)) as { name?: string; email?: string; sub?: string };
      const email = (decoded.email ?? decoded.sub ?? '').trim();
      if (!email) return null;
      const name = (decoded.name ?? '').trim();
      return { email, name: name || email };
    } catch {
      return null;
    }
  }

  /** Splits a full display name into first and last for profile forms. */
  parseNameParts(fullName: string): { firstName: string; lastName: string } {
    const trimmed = fullName.trim();
    if (!trimmed) {
      return { firstName: '', lastName: '' };
    }
    const space = trimmed.indexOf(' ');
    if (space < 0) {
      return { firstName: trimmed, lastName: '' };
    }
    return {
      firstName: trimmed.slice(0, space).trim(),
      lastName: trimmed.slice(space + 1).trim(),
    };
  }

  /**
   * Returns the current user's display name from the JWT (name claim, or email, or 'Guest').
   * Safe to call in browser only; returns null in SSR or when no token.
   */
  getDisplayName(): string | null {
    const claims = this.getJwtClaims();
    return claims?.name ?? null;
  }
}
